import { useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { auth } from '@/lib/firebase'
import {
  CalendarDisconnected, CalendarError, connectGoogleCalendar, disconnectGoogleCalendar, fetchEvents, fetchLinkStatus, loadGis,
  type CalendarEvent, type CalendarLinkStatus,
} from './googleCalendar'

/**
 * Calendar state for components. The link lives on the API, so every tab, browser and device
 * agrees on it through one query; connecting or disconnecting anywhere updates them all.
 */

type Status = 'loading' | 'disconnected' | 'connected' | 'unavailable'
const STATUS_KEY = ['gcal', 'status'] as const

export function useCalendarConnection() {
  const queryClient = useQueryClient()
  const link = useQuery({ queryKey: STATUS_KEY, queryFn: fetchLinkStatus, staleTime: 5 * 60_000, retry: 1 })
  const data = link.data
  // Load Google's library ahead of the click, so its window opens inside it (no popup block).
  useEffect(() => {
    if (data?.available && !data.connected) void loadGis().catch(() => undefined)
  }, [data?.available, data?.connected])

  const set = useCallback((next: CalendarLinkStatus) => {
    queryClient.setQueryData(STATUS_KEY, next)
    void queryClient.invalidateQueries({ queryKey: ['gcal', 'events'] })
  }, [queryClient])
  const connect = useCallback(async () => set(await connectGoogleCalendar(data, auth.currentUser?.email ?? null)), [data, set])
  const disconnectMutation = useMutation({ mutationFn: disconnectGoogleCalendar, onSuccess: set })

  const status: Status = !data ? (link.isError ? 'unavailable' : 'loading') : !data.available ? 'unavailable' : data.connected ? 'connected' : 'disconnected'
  return { status, lapsed: Boolean(data?.lapsed), canWrite: Boolean(data?.canWrite), email: data?.email ?? null, connectedAt: data?.connectedAt ?? null, connect, disconnect: () => disconnectMutation.mutateAsync() }
}

/** Events in [from, to). Refreshes every 5 minutes while connected. */
export function useCalendarEvents(from: Date, to: Date, enabled = true) {
  const queryClient = useQueryClient()
  const { status: s } = useCalendarConnection()
  const query = useQuery({
    queryKey: ['gcal', 'events', from.toISOString(), to.toISOString()],
    queryFn: ({ signal }) => fetchEvents(from, to, signal),
    enabled: enabled && s === 'connected',
    staleTime: 2 * 60_000,
    refetchInterval: 5 * 60_000,
    retry: (count, err) => !(err instanceof CalendarError || err instanceof CalendarDisconnected) && count < 1,
  })
  const ended = query.error instanceof CalendarDisconnected
  // The link ended on Google's side: re-read the status so every surface shows "Connect".
  useEffect(() => {
    if (ended) void queryClient.invalidateQueries({ queryKey: STATUS_KEY })
  }, [ended, queryClient])
  return { ...query, events: (query.data ?? []) as CalendarEvent[], status: s }
}

export function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}
